/**
 * **One survey answer, drawn.** The matrix, the report and the comparison all print the same eight
 * kinds of answer, so they all print them through here.
 *
 * ── The interface is a CELL, not fifteen props ───────────────────────────────────────────────
 * `SurveyCellView` is already the model's answer to "what is in this cell", and handing the whole
 * of it over means a new kind of answer is added in `model/surveyHistory` and appears on all three
 * surfaces at once. The two booleans beside it are the only things a surface genuinely decides:
 * whether a change is printed here (the matrix prints one on a measurement row and nowhere else —
 * the chart above it carries every other trend) and which class carries the varying geometry.
 *
 * No React state, no reads, no decisions: everything it draws was decided in `model/` — including
 * the two words it prints, which arrive as `labels` rather than being looked up here. A matrix of a
 * daily check-in over a year is sixteen thousand of these, and an i18next subscription per cell is a
 * worse cost than the lookup it would save.
 *
 * ── The media is lazy, and that is about a 200-column matrix ────────────────────────────────
 * Every photo and every clip poster carries `loading="lazy"` and `decoding="async"`, and a playable
 * clip `preload="metadata"`: the box is already reserved in CSS, so nothing moves when one lands,
 * and the ones scrolled off the side of the table are never fetched at all.
 */

import photoBox from '~/shared/ui/photoBox.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { SurveyReadingLabels } from '../lib/useReadingLabels';

import { BlankIcon, PlayIcon, ReviewedIcon } from './SurveyIcons';
import type { SurveyCellView, SurveyRowKind } from '../model/surveyHistory';
import { SCALE_STEPS } from '../model/surveyHistory';
import styles from './SurveyReading.module.css';

/**
 * The class a verdict wears, for every signed change on this tab.
 *
 * Exported because five surfaces print one — the matrix cell, the chart head, the report's
 * „vs poprzednia" line, the report rail's sparkline and the comparison's change column — and each
 * had its own copy of this map over its own copy of the two colour classes. `flat` is deliberately
 * the empty string: an undirected change keeps its surface's neutral ink rather than being painted.
 */
export const TONE_CLASS = {
  good: String(styles.good),
  warn: String(styles.warn),
  flat: '',
} as const;

interface SurveyReadingProps {
  readonly cell: SurveyCellView;
  readonly kind: SurveyRowKind;
  /** Print the change beside the figure. See the header for why this is the caller's call. */
  readonly showDelta: boolean;
  /**
   * Print a rating's FIGURE beside its five dashes — „4 /5".
   *
   * The canon draws it in the report and the comparison and not in the matrix, and that is a
   * reading decision rather than a size one: a matrix cell is scanned down a column, where five
   * dashes compare at a glance and a figure beside each one is noise; a report line is read on its
   * own, where the dashes alone make the trainer count them.
   */
  readonly showScaleFigure?: boolean;
  /**
   * The word for „nie odpowiedziano" and the one an aged-out clip's frame carries.
   *
   * Handed in rather than looked up here, and that is a measured choice: this component is called
   * once per CELL, and `useTranslation` inside it would be an i18next subscription per cell. The
   * three surfaces call `lib/useReadingLabels` once per render and pass the pair down.
   */
  readonly labels: SurveyReadingLabels;
  /** The geometry class for a photo strip or a clip frame — see `SurveyReading.module.css`. */
  readonly mediaClassName?: string;
  /** Draw a clip as a real player rather than as a poster. The two dialogs do; the matrix does not. */
  readonly playable?: boolean;
  /**
   * Name each photograph's pose under it, in words.
   *
   * The pose is the only thing an `alt` on a progress photograph can honestly say, and the report
   * says it VISIBLY as well: the canon labels the three frames PRZÓD / BOK / TYŁ, and a trainer
   * reading two photographs side by side cannot otherwise tell a side view from a back one. Where
   * the caption is drawn the `alt` goes empty, or the pose is announced twice.
   */
  readonly poseCaptions?: boolean;
}

export function SurveyReading({
  cell,
  kind,
  showDelta,
  showScaleFigure,
  labels,
  mediaClassName,
  playable,
  poseCaptions,
}: SurveyReadingProps) {
  if (cell.state !== 'value') {
    return (
      <span
        className={`${String(styles.blank)} ${cell.state === 'absent' ? String(styles.absent) : ''}`}
        title={labels.blankLabel}
      >
        <BlankIcon />
        <span className={hidden.srOnly}>{labels.blankLabel}</span>
      </span>
    );
  }

  if (kind === 'scale') {
    const filled = cell.scaleFilled ?? 0;
    const dashes = (
      // The rating is drawn as five dashes and READ as a number: the group carries the figure as
      // its accessible name, and the dashes themselves are decoration.
      <span className={styles.scale} role="img" aria-label={`${cell.numberText ?? ''}/${String(SCALE_STEPS)}`}>
        {Array.from({ length: SCALE_STEPS }, (_, step) => (
          <i
            key={step}
            className={`${String(styles.step)} ${step < filled ? '' : String(styles.stepOff)}`}
          />
        ))}
      </span>
    );
    if (showScaleFigure !== true) return dashes;
    return (
      <span className={styles.rating}>
        <b className={styles.figure}>
          {cell.numberText}
          <span className={styles.unit}>{`/${String(SCALE_STEPS)}`}</span>
        </b>
        {dashes}
      </span>
    );
  }

  if (kind === 'boolean') {
    return (
      <span
        className={`${String(styles.mark)} ${cell.bool === true ? String(styles.markYes) : String(styles.markNo)}`}
        role="img"
        aria-label={cell.text ?? ''}
      >
        {cell.bool === true ? <ReviewedIcon /> : <BlankIcon />}
      </span>
    );
  }

  if (kind === 'photos') {
    return (
      <span className={`${String(styles.photos)} ${mediaClassName ?? ''}`}>
        {cell.photos.map((photo) =>
          poseCaptions === true ? (
            <span className={styles.pose} key={photo.pose}>
              <img
                className={`${String(photoBox.photo)} ${String(styles.photo)}`}
                src={photo.url}
                alt=""
                loading="lazy"
                decoding="async"
              />
              <em className={styles.poseName}>{photo.alt}</em>
            </span>
          ) : (
            <img
              key={photo.pose}
              className={`${String(photoBox.photo)} ${String(styles.photo)}`}
              src={photo.url}
              alt={photo.alt}
              loading="lazy"
              decoding="async"
            />
          ),
        )}
      </span>
    );
  }

  if (kind === 'clip') {
    const clip = cell.clip;
    if (clip === null) return null;
    if (clip.expired) {
      return (
        <span className={`${String(styles.clip)} ${String(styles.clipExpired)} ${mediaClassName ?? ''}`}>
          {labels.expiredLabel}
        </span>
      );
    }
    return (
      <span className={`${String(styles.clip)} ${mediaClassName ?? ''}`}>
        {playable === true && clip.videoUrl !== null ? (
          <video
            className={`${String(photoBox.photo)} ${String(styles.clipVideo)}`}
            src={clip.videoUrl}
            poster={clip.posterUrl ?? undefined}
            controls
            preload="metadata"
          />
        ) : (
          <>
            {clip.posterUrl === null ? null : (
              <img
                className={`${String(photoBox.photo)} ${String(styles.clipVideo)}`}
                src={clip.posterUrl}
                alt=""
                loading="lazy"
                decoding="async"
              />
            )}
            <span className={styles.clipPlay} aria-hidden="true">
              <PlayIcon />
            </span>
          </>
        )}
        {clip.durationLabel === null ? null : (
          <em className={styles.clipDuration}>{clip.durationLabel}</em>
        )}
      </span>
    );
  }

  if (cell.numberText === null) {
    return <span className={styles.text}>{cell.text}</span>;
  }

  return (
    <span className={styles.reading}>
      <b className={styles.figure}>
        {cell.numberText}
        {cell.unit === null ? null : <span className={styles.unit}>{cell.unit}</span>}
      </b>
      {showDelta && cell.deltaText !== null ? (
        <em className={`${String(styles.delta)} ${TONE_CLASS[cell.deltaTone ?? 'flat']}`}>
          {cell.deltaText}
        </em>
      ) : null}
    </span>
  );
}
